import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { StyledProps } from 'styled-components'
import { useOnClickOutside } from '../../../hooks/useOnClickOutside'
import { Button } from '../../buttons/Button/Button'
import { IconTriangleDown } from '../../iconography/Icons/Icons'
import { SwitchSelect } from '../../selects/SwitchSelect/SwitchSelect'
import { Tabs } from '../Tabs/Tabs'

const Wrapper = styled.div<StyledProps<{ showTabsSelect?: boolean }>>`
  position: relative;
  display: flex;
  align-items: baseline;
  gap: 24px;
  padding-right: ${({ showTabsSelect }) => (showTabsSelect ? '24px' : '170px')};
  width: 100%;
`

const TabsWrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  gap: 24px;
  overflow: hidden;
  width: 100%;
  white-space: nowrap;
  margin-left: 24px;

  & > [class^='Wrapper'] {
    overflow-x: auto;
    padding: 0;
  }
`

const MoreButton = styled(Button)`
  min-width: 160px;
  min-height: 32px;
  background-color: ${props => props.theme.color.primary50};

  & svg {
    width: 10px;
    height: 5px;
    fill: ${props => props.theme.color.primary600};
  }

  &:hover {
    background-color: ${props => props.theme.color.primary100};
  }
`

const IconTriangleUp = styled(IconTriangleDown)`
  color: ${props => props.theme.color.primary600};
  transform: rotate(180deg);
`

const SwitchSelectWrapper = styled.div`
  position: absolute;
  right: 41px;
  top: 50px;
  width: 280px;
  height: fit-content;
  overflow: visible;
`

type TabOption = {
  title: string
  value: string
}

type TabsWithSelectProps = {
  tabsOptions: TabOption[]
  changeTabsText?: string
  tabsMoreText?: string
  showingLabel?: string
  showTabsSelect?: boolean
  activeTab?: string
  selectedOptions?: string[]
  onTabChange: (value: string) => void
  onSelectedOptionsChange: (options: string[]) => void
}

export const TabsWithSelect: React.FC<TabsWithSelectProps> = ({
  tabsOptions,
  changeTabsText,
  tabsMoreText,
  showingLabel,
  showTabsSelect = true,
  activeTab,
  selectedOptions,
  onTabChange,
  onSelectedOptionsChange,
}) => {
  const { t } = useTranslation('ui')
  const [initialTabChosen, setInitialTabChosen] = useState(!!activeTab)
  const selectOptions = useMemo(() => {
    return tabsOptions.map(tab => ({ id: tab.value, title: tab.title }))
  }, [tabsOptions])

  const [selectedOptionsState, setSelectedOptionsState] = useState(selectedOptions)

  useEffect(() => {
    if (!selectedOptions) {
      setSelectedOptionsState(selectOptions.map(o => o.id))
    }
  }, [selectOptions, selectedOptions])

  const tabsState = useMemo(() => {
    return showTabsSelect
      ? tabsOptions.filter((tab: TabOption) => selectedOptionsState?.includes(tab.value))
      : tabsOptions
  }, [selectedOptionsState, showTabsSelect, tabsOptions])

  useEffect(() => {
    if (!!tabsState.length && !initialTabChosen) {
      onTabChange(tabsState[0].value)
      setInitialTabChosen(true)
    }
  }, [initialTabChosen, onTabChange, tabsState, tabsState.length])

  const [showDropdown, setShowDropdown] = useState(false)
  const ref = useRef(null)
  useOnClickOutside(ref, () => setShowDropdown(false))

  const moreButtonText = useMemo(() => {
    const unselectedCount = selectOptions.length - tabsState.length
    return tabsState.length === selectOptions.length
      ? changeTabsText || t('TabsWithSelect:changeTabs')
      : `+${unselectedCount} ${tabsMoreText || t('TabsWithSelect:tabsMore')}`
  }, [changeTabsText, selectOptions.length, t, tabsMoreText, tabsState.length])

  const onSelectOptionChange = useCallback(
    (value: string) => {
      const saved = [...(selectedOptionsState || [])]

      if (!selectedOptionsState?.includes(value)) {
        saved.push(value)
      } else {
        saved.splice(saved.indexOf(value), 1)
      }

      setSelectedOptionsState(saved)
      onSelectedOptionsChange(saved)
    },
    [onSelectedOptionsChange, selectedOptionsState],
  )

  return (
    <Wrapper showTabsSelect={showTabsSelect}>
      <TabsWrapper>
        <Tabs options={tabsState} active={activeTab || ''} onChange={onTabChange} />
      </TabsWrapper>
      {showTabsSelect && (
        <>
          <MoreButton intent="secondary" size="small" filled onClick={() => setShowDropdown(!showDropdown)}>
            {moreButtonText}
            {showDropdown ? <IconTriangleUp /> : <IconTriangleDown />}
          </MoreButton>
          {showDropdown && (
            <SwitchSelectWrapper ref={ref}>
              <SwitchSelect
                options={selectOptions}
                selected={selectedOptionsState}
                onChange={onSelectOptionChange}
                onMenuClose={() => setShowDropdown(false)}
                showingLabel={showingLabel || t('TabsWithSelect:showing')}
              />
            </SwitchSelectWrapper>
          )}
        </>
      )}
    </Wrapper>
  )
}
